import { useState } from 'react';
import PropTypes from 'prop-types';
import { matchPath, useNavigate } from 'react-router-dom';
import { Box, Collapse, Divider, List } from '@material-ui/core';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import NavItem from './NavItem';
import { addTab } from "../store/tabSlice";
import { useDispatch } from "react-redux";
import { lightVeil, getTone } from '../theme/tones';

/**
 * Les rubriques repliées survivent à la navigation et au rechargement :
 * replier « Qualité » parce qu'on ne s'en sert pas est un réglage, pas un
 * geste à refaire à chaque page.
 */
const STORAGE_KEY = 'sidebar.collapsedSections';

const readCollapsed = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch (e) {
    return [];
  }
};

const writeCollapsed = (title, collapsed) => {
  const current = readCollapsed().filter((t) => t !== title);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed ? [...current, title] : current));
};

/** Vrai si la route courante vit dans cette rubrique (à n'importe quelle profondeur). */
const containsPath = (items, pathname) => items.some((item) => {
  if (item.children && containsPath(item.children, pathname)) return true;
  return item.path ? !!matchPath({ path: item.path, end: !item.children }, pathname) : false;
});

const renderNavItems = ({ depth = 0, items, pathname, drawerOpen, openMobile, dispatch, navigate }) => (
  <List disablePadding>
    {items.reduce(
      // eslint-disable-next-line no-use-before-define
      (acc, item) => reduceChildRoutes({
        acc,
        item,
        pathname,
        depth,
        drawerOpen,
        openMobile,
        dispatch,
        navigate
      }), []
    )}
  </List>
);

const reduceChildRoutes = ({ acc, pathname, item, depth, drawerOpen, openMobile, dispatch, navigate }) => {
  const key = `${item.title}-${depth}`;
  const exactMatch = item.path ? !!matchPath({ path: item.path, end: true }, pathname) : false;

  const handleClick = () => {
    if (item.path) {
      dispatch(addTab({ id: item.path, label: item.title, path: item.path }));
      navigate(item.path);
    }
  };

  if (item.children) {
    const partialMatch = item.path ? !!matchPath({ path: item.path, end: false }, pathname) : false;

    acc.push(
      <NavItem
        active={partialMatch}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={partialMatch}
        path={item.path}
        title={item.title}
        drawerOpen={drawerOpen}
        openMobile={openMobile}
        onClick={handleClick}
      >
        {renderNavItems({
          depth: depth + 1,
          items: item.children,
          pathname,
          drawerOpen,
          openMobile,
          dispatch,
          navigate
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        active={exactMatch}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        path={item.path}
        title={item.title}
        drawerOpen={drawerOpen}
        openMobile={openMobile}
        onClick={handleClick}
      />
    );
  }

  return acc;
};

/**
 * Une rubrique du rail : un en-tête repliable et ses entrées.
 *
 * Replié en rail d'icônes (`drawerOpen` faux), l'en-tête disparaît et les
 * entrées restent toujours visibles — sans en-tête il n'y aurait plus rien
 * pour les rouvrir.
 */
const NavSection = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, pathname, title, drawerOpen, openMobile, ...other } = props;
  const expanded = drawerOpen || openMobile;
  const [collapsed, setCollapsed] = useState(() => readCollapsed().includes(title));

  const toggle = () => {
    setCollapsed((prev) => {
      writeCollapsed(title, !prev);
      return !prev;
    });
  };

  // Rubrique repliée mais route courante dedans : un point le signale, pour
  // qu'on ne cherche pas l'entrée active ailleurs.
  const hidesActive = collapsed && containsPath(items, pathname);

  return (
    <Box {...other}>
      {expanded ? (
        <Box
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
          aria-expanded={!collapsed}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            mb: 0.25,
            borderRadius: '8px',
            cursor: 'pointer',
            userSelect: 'none',
            color: lightVeil(0.45),
            fontSize: '0.63rem',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            transition: 'color .2s ease, background-color .2s ease',
            '&:hover': { color: lightVeil(0.85), backgroundColor: lightVeil(0.05) },
            '&:hover .section-chevron': { color: lightVeil(0.85) }
          }}
        >
          <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </Box>
          {hidesActive && (
            <Box
              component="span"
              sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getTone('blue').light, flexShrink: 0 }}
            />
          )}
          <Box component="span" sx={{ flex: 1, height: '1px', bgcolor: lightVeil(0.1) }} />
          <ExpandMoreRoundedIcon
            className="section-chevron"
            sx={{
              fontSize: 16,
              color: lightVeil(0.4),
              transition: 'transform .25s cubic-bezier(0.16, 1, 0.3, 1), color .2s ease',
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
            }}
          />
        </Box>
      ) : (
        <Divider sx={{ my: 1, mx: 1.5, borderColor: lightVeil(0.1) }} />
      )}

      <Collapse in={!expanded || !collapsed} timeout={220} unmountOnExit={false}>
        {renderNavItems({
          items,
          pathname,
          drawerOpen,
          openMobile,
          dispatch,
          navigate
        })}
      </Collapse>
    </Box>
  );
};

NavSection.propTypes = {
  items: PropTypes.array,
  pathname: PropTypes.string,
  title: PropTypes.string,
  drawerOpen: PropTypes.bool,
  openMobile: PropTypes.bool
};

export default NavSection;
